import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { enterPartnerContext } from '../lib/partnerContext';
import Sidebar from '../components/Sidebar';

interface Analytics {
  totalClients: number;
  totalProjects: number;
  totalFailingGates: number;
}

interface ClientTenant {
  id: string;
  clientTenantId: string;
  deliveryModel: 'SELF_SERVICE' | 'MANAGED';
  tenant: { name: string };
  activeProjectCount: number;
  gateHealth: 'PASSING' | 'FAILING' | 'NO_GATES';
  failingGateCount: number;
  overdueApprovals: number;
  criticalItems: number;
  stalledPhases: number;
  lastActivityAt: string | null;
  currentPhase: string | null;
  linkedAt: string;
}

interface ActivityItem {
  id: string;
  type: 'gate_evaluation' | 'artefact_update';
  subject: string;
  projectName: string;
  tenantName: string;
  entityId: string;
  result: string | null;
  timestamp: string;
}

const HEALTH_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  PASSING:  { bg: 'rgba(34,197,94,0.1)',  text: '#22c55e', label: 'Healthy' },
  FAILING:  { bg: 'rgba(245,158,11,0.1)', text: '#f59e0b', label: 'Needs Attention' },
  NO_GATES: { bg: 'rgba(107,114,128,0.1)', text: '#6b7280', label: 'Awaiting Evaluation' },
};

// Sprint 11 UX.A1: a regex like /^phase_\d+_/ strips only the first digit
// segment, so `phase_2_5_ux_design` would render as "5 Ux Design". Use an
// explicit map; the title-cased fallback covers any future phase that lands
// on this page before the map is updated.
const PHASE_LABEL: Record<string, string> = {
  phase_0_bootstrap:        'Bootstrap',
  phase_1_requirements:     'Requirements',
  phase_2_architecture:     'Architecture',
  phase_2_5_ux_design:      'UX Design',
  phase_3_sprint_planning:  'Sprint Planning',
  phase_4_development:      'Development',
  phase_5_code_review:      'Code Review',
  phase_6_testing:          'Testing',
  phase_7_deployment:       'Deployment',
  phase_8_retrospective:    'Retrospective',
};

function formatPhase(phase: string | null): string {
  if (!phase) return '--';
  return PHASE_LABEL[phase] ?? phase.replace(/^phase_[\d_]+_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'No activity';
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

const TYPE_LABELS: Record<string, string> = {
  gate_evaluation: 'Gate',
  artefact_update: 'Artefact',
};

export default function ClientManagementPage() {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [clients, setClients] = useState<ClientTenant[]>([]);
  const [activityItems, setActivityItems] = useState<ActivityItem[]>([]);
  const [actionableCount, setActionableCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [panelError, setPanelError] = useState(false);
  const navigate = useNavigate();
  const { user, setSession } = useAuth();

  useEffect(() => {
    // Primary: client list (blocking)
    apiFetch<{ analytics: Analytics; clients: ClientTenant[] }>('/partner/clients')
      .then(data => {
        setAnalytics(data.analytics);
        setClients(data.clients);
      })
      .catch(err => setError(err.message))
      .finally(() => setIsLoading(false));

    // Secondary: notifications panel (non-blocking, degrades independently)
    apiFetch<{ items: ActivityItem[]; actionableCount: number }>('/partner/notifications/panel')
      .then(panelData => {
        setActivityItems(panelData.items);
        setActionableCount(panelData.actionableCount);
      })
      .catch(() => setPanelError(true));
  }, []);

  const handleSwitchContext = async (clientTenantId: string) => {
    const result = await apiFetch<{ contextToken: string; tenantId: string; partnerAccountId: string }>(
      `/partner/clients/${clientTenantId}/switch`,
      { method: 'POST' },
    );
    if (!user) return;
    // #1330: sync the scoped token through AuthContext (setSession) so
    // useAuth().token tracks the client context without a full page reload.
    enterPartnerContext(
      { contextToken: result.contextToken, partnerAccountId: result.partnerAccountId, returnPath: '/partner/clients' },
      user,
      setSession,
    );
    navigate('/dashboard');
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar />
      <main className="flex-1 p-6" style={{ backgroundColor: 'var(--bg)' }}>
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>
                Client Management
              </h1>
              <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
                Monitor client health, projects, and notifications
              </p>
            </div>
            <button
              onClick={() => navigate('/partner')}
              className="px-4 py-2 rounded-lg text-sm font-medium"
              style={{ border: '1px solid var(--border)', color: 'var(--text-muted)' }}
            >
              Back to Portal
            </button>
          </div>

          {isLoading && (
            <p style={{ color: 'var(--text-muted)' }}>Loading client data...</p>
          )}

          {error && (
            <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#ef4444' }}>
              {error}
            </div>
          )}

          {!isLoading && !error && (
            <>
              {/* Analytics Bar */}
              {analytics && (
                <div
                  className="grid grid-cols-3 gap-4 mb-6"
                  data-testid="analytics-bar"
                >
                  <div className="rounded-lg p-4" style={{ border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)' }}>
                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Total Clients</p>
                    <p className="text-2xl font-bold mt-1" style={{ color: 'var(--text)' }}>{analytics.totalClients}</p>
                  </div>
                  <div className="rounded-lg p-4" style={{ border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)' }}>
                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Active Projects</p>
                    <p className="text-2xl font-bold mt-1" style={{ color: 'var(--text)' }}>{analytics.totalProjects}</p>
                  </div>
                  <div className="rounded-lg p-4" style={{ border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)' }}>
                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Failing Gates</p>
                    <p className="text-2xl font-bold mt-1" style={{ color: analytics.totalFailingGates > 0 ? '#f59e0b' : 'var(--text)' }}>
                      {analytics.totalFailingGates}
                    </p>
                  </div>
                </div>
              )}

              {/* Client Cards + Notification Panel */}
              <div className="flex gap-6">
                {/* Client List */}
                <div className="flex-1 space-y-3">
                  <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text)' }}>Clients</h2>
                  {clients.length === 0 && (
                    <div className="border border-dashed rounded-lg p-12 text-center" style={{ borderColor: 'var(--border)' }}>
                      <p style={{ color: 'var(--text-muted)' }}>No client tenants linked yet.</p>
                    </div>
                  )}
                  {clients.map(client => {
                    const health = HEALTH_COLORS[client.gateHealth] ?? HEALTH_COLORS.PASSING;
                    return (
                      <div
                        key={client.id}
                        className="border rounded-lg p-4 transition-colors"
                        style={{
                          borderColor: 'var(--border)',
                          backgroundColor: 'var(--card-bg)',
                          borderLeft: `3px solid ${health.text}`,
                        }}
                        data-testid="client-card"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold" style={{ color: 'var(--text)' }}>
                                {client.tenant.name}
                              </span>
                              <span
                                className="text-xs px-2 py-0.5 rounded-full"
                                style={{ backgroundColor: health.bg, color: health.text }}
                                data-testid="health-badge"
                              >
                                {client.gateHealth === 'FAILING'
                                  ? `${client.failingGateCount} gate${client.failingGateCount !== 1 ? 's' : ''} failing`
                                  : client.gateHealth === 'NO_GATES'
                                    ? 'Awaiting Evaluation'
                                    : health.label}
                              </span>
                              <span
                                className="text-xs px-2 py-0.5 rounded-full"
                                style={{ backgroundColor: 'var(--bg-muted)', color: 'var(--text-muted)' }}
                              >
                                {client.deliveryModel === 'MANAGED' ? 'Managed' : 'Self-Service'}
                              </span>
                              {client.overdueApprovals > 0 && (
                                <span
                                  className="text-xs px-2 py-0.5 rounded-full"
                                  style={{ backgroundColor: 'rgba(245,158,11,0.12)', color: '#d97706' }}
                                >
                                  {client.overdueApprovals} overdue
                                </span>
                              )}
                              {client.criticalItems > 0 && (
                                <span
                                  className="text-xs px-2 py-0.5 rounded-full"
                                  style={{ backgroundColor: 'rgba(239,68,68,0.12)', color: '#dc2626' }}
                                >
                                  {client.criticalItems} critical
                                </span>
                              )}
                              {client.stalledPhases > 0 && (
                                <span
                                  className="text-xs px-2 py-0.5 rounded-full"
                                  style={{ backgroundColor: 'rgba(245,158,11,0.12)', color: '#d97706' }}
                                >
                                  {client.stalledPhases} stalled
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                              <span>{client.activeProjectCount} project{client.activeProjectCount !== 1 ? 's' : ''}</span>
                              <span>Phase: {formatPhase(client.currentPhase)}</span>
                              <span>{timeAgo(client.lastActivityAt)}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => navigate(`/partner/clients/${client.clientTenantId}`)}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium"
                              style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
                            >
                              Details
                            </button>
                            <button
                              onClick={() => handleSwitchContext(client.clientTenantId)}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium"
                              style={{ border: '1px solid var(--primary)', color: 'var(--primary-light)', backgroundColor: 'transparent' }}
                            >
                              Open
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Notification Panel */}
                <div className="w-80 flex-shrink-0">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-lg font-semibold" style={{ color: 'var(--text)' }}>Activity</h2>
                    {actionableCount > 0 && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full font-medium"
                        style={{ backgroundColor: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}
                        data-testid="actionable-badge"
                      >
                        {actionableCount} failing gate{actionableCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <div
                    className="border rounded-lg overflow-hidden"
                    style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card-bg)' }}
                    data-testid="activity-panel"
                  >
                    {panelError ? (
                      <p className="p-4 text-sm text-center" style={{ color: 'var(--text-muted)' }}>
                        Activity feed unavailable
                      </p>
                    ) : activityItems.length === 0 ? (
                      <p className="p-4 text-sm text-center" style={{ color: 'var(--text-muted)' }}>
                        No recent activity
                      </p>
                    ) : (
                      <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
                        {activityItems.slice(0, 10).map(item => (
                          <div key={item.id} className="p-3">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-xs font-medium" style={{ color: 'var(--primary)' }}>
                                {TYPE_LABELS[item.type] ?? item.type}
                              </span>
                              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                {item.tenantName}
                              </span>
                              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                {timeAgo(item.timestamp)}
                              </span>
                            </div>
                            <p className="text-xs" style={{ color: 'var(--text)' }}>
                              {item.subject}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
